import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useContentStudioApi } from '../../../common/hooks/use-content-studio-api.hook';
import { useAssistantApi } from '../../../common/hooks/use-assistant-api.hook';
import { CsAgentRun, CsAgentSideEffect } from '../../../common/models/content-studio.model';
import { useAskAi } from '../assistant/AskAiContext';
import { ActionTrail } from '../content-studio/planner/AgentTurnCards';
import { relativeTime } from '../insights/insight-presentation';
import {
    WaitingItem,
    describeStep,
    profileLabelKey,
    proposedSteps,
    runRoute,
    stepVerb,
} from './work-presentation';
import { useAgentWork } from './use-agent-work.hook';

const CLASS_TONE: Record<CsAgentSideEffect, string> = {
    read: 'bg-muted text-muted-foreground',
    write: 'bg-muted text-foreground',
    spend: 'bg-warning/15 text-warning',
    publish: 'bg-destructive/10 text-destructive',
};

const STATUS_TONE: Record<CsAgentRun['status'], string> = {
    pending: 'bg-muted text-muted-foreground',
    running: 'bg-muted text-foreground',
    // A decision is outstanding, so it reads like the spend it is waiting on.
    paused: 'bg-warning/15 text-warning',
    succeeded: 'bg-success/15 text-success',
    failed: 'bg-destructive/10 text-destructive',
    cancelled: 'bg-muted text-muted-foreground',
};

/**
 * What the agents are doing on the vendor's behalf.
 *
 * A board rather than a page: it is opened from the dashboard's AI section in
 * a dialog. A whole route for it was a place vendors had to remember to visit,
 * which is the opposite of what an agent surface should be — the work should
 * come to the screen they are already on.
 *
 * Three lanes, in the order a vendor needs them: what is waiting for them,
 * what is running, what finished. Every profile lands here — the studio
 * planner today, the assistant's proposals, whatever comes next — because a
 * vendor should never have to know which module to open to learn whether
 * something is done.
 */
export const WorkBoard: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const studio = useContentStudioApi();
    const assistant = useAssistantApi();
    const askAi = useAskAi();

    const { waiting, running, done, loading, failed: loadFailed, reload } = useAgentWork();
    const [busy, setBusy] = useState<string | null>(null);
    /* Approving a step can fail on its own; the hook only knows about
       loading. Both surface as the same sentence, as they did before. */
    const [actionFailed, setActionFailed] = useState(false);
    const failed = loadFailed || actionFailed;

    /** A step the agent proposed on a run — a publish, a spend — approved or declined here. */
    const resolveStep = async (run: CsAgentRun, index: number, how: 'approve' | 'decline') => {
        const key = `step:${run.runId}:${index}`;
        setBusy(key);
        try {
            if (how === 'approve') await studio.approveAgentStep(run.runId, index);
            else await studio.declineAgentStep(run.runId, index);
            await reload();
        } catch {
            setActionFailed(true);
        } finally {
            setBusy(null);
        }
    };

    const resolveAction = async (item: WaitingItem, how: 'apply' | 'decline') => {
        if (!item.action) return;
        setBusy(item.key);
        try {
            if (how === 'apply') await assistant.applyAction(item.action.threadUuid, item.action.actionId);
            else await assistant.declineAction(item.action.threadUuid, item.action.actionId);
            await reload();
        } catch {
            setActionFailed(true);
        } finally {
            setBusy(null);
        }
    };

    const card = 'flex flex-col gap-2.5 border bg-card p-5';

    const chip = (label: string, tone: string) => (
        <span className={cn('inline-flex items-center px-2 py-0.5 text-[11.5px] font-medium whitespace-nowrap', tone)}>
            {label}
        </span>
    );

    const when = (iso: string) => {
        const r = relativeTime(iso);
        return t(r.key, { count: r.count });
    };

    const meta = (children: React.ReactNode) => (
        <span className="text-xs text-muted-foreground">{children}</span>
    );

    const trail = (run: CsAgentRun, live = false) =>
        run.steps?.length ? (
            <ActionTrail actions={run.steps.map((s) => ({ verb: stepVerb(s.tool) }))} live={live} />
        ) : null;

    const actionButton = (label: string, onClick: () => void, primary = false, disabled = false) => (
        <Button
            onClick={onClick}
            variant="outline"
            size="sm"
            disabled={disabled}
            className={cn(!primary && 'text-muted-foreground', primary && 'border-foreground')}
        >
            {label}
        </Button>
    );

    const section = (title: string, count: number, body: React.ReactNode, empty: string) => (
        <div className="flex flex-col gap-3">
            <div className="flex items-baseline gap-2">
                <p className="text-[15px] font-medium">{title}</p>
                {meta(count)}
            </div>
            {count ? body : <p className="text-[13.5px] text-muted-foreground">{empty}</p>}
        </div>
    );

    const waitingCard = (item: WaitingItem) => {
        const route = item.run ? runRoute(item.run) : null;
        const applyLabel = t(item.sideEffect === 'publish' ? 'WORK.PUBLISH' : 'WORK.APPLY');
        return (
            <div
                key={item.key}
                className={cn(
                    card,
                    // Ink outline: this card is asking for a decision.
                    'outline outline-[1.5px] -outline-offset-[1.5px] outline-foreground',
                    busy === item.key && 'opacity-60',
                )}
            >
                <div className="flex flex-wrap items-center gap-2">
                    {chip(t(`WORK.CLASS.${item.sideEffect.toUpperCase()}`), CLASS_TONE[item.sideEffect])}
                    {item.creditCost != null && chip(t('WORK.COST', { count: item.creditCost }), CLASS_TONE.spend)}
                    <div className="flex-1" />
                    {meta(when(item.at))}
                </div>
                <p className="text-sm leading-snug font-medium">{item.title}</p>
                {meta(item.source === 'assistant'
                    ? `${t('WORK.PROFILE.ASSISTANT')}${item.action?.threadTitle ? ` · ${item.action.threadTitle}` : ''}`
                    : t(profileLabelKey(item.run!.profile)))}
                {item.run && trail(item.run)}
                {/* Each proposed step is its own decision: what it would do, and two buttons. */}
                {item.run && proposedSteps(item.run).map((step) => (
                    <div key={step.index} className="flex flex-col gap-2 border bg-background p-3">
                        <div className="flex items-center gap-2">
                            {chip(t(`WORK.CLASS.${step.sideEffect.toUpperCase()}`), CLASS_TONE[step.sideEffect])}
                            {meta(stepVerb(step.tool))}
                        </div>
                        <p className="text-[13.5px] leading-normal text-muted-foreground">{describeStep(step, t as never)}</p>
                        <div className="flex gap-2">
                            {actionButton(
                                t(step.sideEffect === 'publish' ? 'WORK.PUBLISH' : 'WORK.APPLY'),
                                () => void resolveStep(item.run!, step.index, 'approve'),
                                true,
                                busy === `step:${item.run!.runId}:${step.index}`,
                            )}
                            {actionButton(t('WORK.DECLINE'), () => void resolveStep(item.run!, step.index, 'decline'), false, busy === `step:${item.run!.runId}:${step.index}`)}
                        </div>
                    </div>
                ))}
                <div className="flex flex-wrap gap-2">
                    {item.action && actionButton(applyLabel, () => void resolveAction(item, 'apply'), true, busy === item.key)}
                    {item.action && actionButton(t('WORK.DECLINE'), () => void resolveAction(item, 'decline'), false, busy === item.key)}
                    {item.action && askAi && actionButton(t('WORK.OPEN_CONVERSATION'), () => askAi.openThread(item.action!.threadUuid))}
                    {route && actionButton(t('WORK.OPEN_STUDIO'), () => navigate(route), true)}
                </div>
            </div>
        );
    };

    const runCard = (run: CsAgentRun) => {
        const route = runRoute(run);
        const live = run.status === 'running' || run.status === 'pending';
        return (
            <div key={run.runId} className={card}>
                <div className="flex flex-wrap items-center gap-2">
                    {chip(t(`WORK.STATUS.${run.status.toUpperCase()}`), STATUS_TONE[run.status])}
                    {meta(t(profileLabelKey(run.profile)))}
                    <div className="flex-1" />
                    {meta(when(run.updatedAt || run.createdAt))}
                </div>
                <p className="text-sm leading-snug font-medium">{run.message}</p>
                {run.error && <p className="text-[13px] text-destructive">{run.error}</p>}
                {typeof (run.result as { summary?: unknown } | undefined)?.summary === 'string' && (
                    <p className="text-[13.5px] leading-relaxed text-muted-foreground">
                        {String((run.result as { summary: string }).summary)}
                    </p>
                )}
                {trail(run, live)}
                {route && <div>{actionButton(t('WORK.OPEN_STUDIO'), () => navigate(route))}</div>}
            </div>
        );
    };

    return (
        <div className="flex flex-col gap-8">

            {loading && (
                <div className="flex justify-center py-12">
                    <Loader2 className="size-5 animate-spin text-muted-foreground" />
                </div>
            )}

            {!loading && failed && (
                <p className="text-[13.5px] text-destructive">{t('WORK.ERROR')}</p>
            )}

            {!loading && (
                <>
                    {section(
                        t('WORK.SECTION.WAITING'),
                        waiting.length,
                        <div className="grid gap-3 md:grid-cols-2">{waiting.map(waitingCard)}</div>,
                        t('WORK.EMPTY.WAITING'),
                    )}
                    {section(
                        t('WORK.SECTION.RUNNING'),
                        running.length,
                        <div className="flex flex-col gap-3">{running.map(runCard)}</div>,
                        t('WORK.EMPTY.RUNNING'),
                    )}
                    {section(
                        t('WORK.SECTION.DONE'),
                        done.length,
                        <div className="flex flex-col gap-3">{done.slice(0, 20).map(runCard)}</div>,
                        t('WORK.EMPTY.DONE'),
                    )}
                </>
            )}
        </div>
    );
};

